<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <teleport to="#app">
    <oxd-dialog
      v-if="show"
      class="orangehrm-dialog-modal"
      @update:show="onCancel"
    >
      <div class="orangehrm-modal-header">
        <oxd-text type="card-title">
          {{ $t('expensa.select_expenses') }}
        </oxd-text>
      </div>
      <oxd-divider />
      <oxd-text v-if="!isLoading && rows.length === 0">
        {{ $t('expensa.no_free_expenses') }}
      </oxd-text>
      <div v-else class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checked"
          :items="rows"
          :headers="headers"
          :selectable="true"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onCancel"
        />
        <oxd-button
          type="button"
          display-type="secondary"
          :label="$t('expensa.add_selected')"
          :disabled="checked.length === 0 || saving"
          @click="onAdd"
        />
      </oxd-form-actions>
    </oxd-dialog>
  </teleport>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {expensaApi, errorMessage} from '../util/api';
import {day, eur} from '../util/format';
import {kindKey} from '../util/labels';

// Le spese libere del titolare (non ancora in nessuna nota), da aggiungere alla nota.
export default {
  name: 'ExpensaAddExpensesDialog',
  components: {
    'oxd-dialog': OxdDialog,
  },
  props: {
    reportId: {
      type: Number,
      required: true,
    },
    empNumber: {
      type: Number,
      required: true,
    },
  },
  emits: ['updated'],
  data() {
    return {
      show: false,
      isLoading: false,
      saving: false,
      expenses: [],
      checked: [],
      headers: [
        {name: 'date', title: this.$t('general.date'), style: {flex: 2}},
        {name: 'kind', title: this.$t('expensa.col_kind'), style: {flex: 2}},
        {
          name: 'category',
          title: this.$t('expensa.col_category'),
          style: {flex: 3},
        },
        {
          name: 'merchant',
          title: this.$t('expensa.col_merchant'),
          cellType: 'oxd-table-cell-truncate',
          style: {flex: 4},
        },
        {
          name: 'amount',
          title: this.$t('expensa.col_amount_eur'),
          style: {flex: 2},
        },
      ],
    };
  },
  computed: {
    rows() {
      return this.expenses.map((e) => ({
        id: e.id,
        date: day(e.date),
        kind: this.$t(kindKey(e.kind)),
        category: e.categoryName ?? '—',
        merchant: e.merchant || e.description || '—',
        amount: eur(e.amountEur),
      }));
    },
  },
  methods: {
    async showDialog() {
      this.checked = [];
      this.expenses = [];
      this.show = true;
      this.isLoading = true;
      try {
        const {data} = await expensaApi('/expenses').getAll({
          unreported: true,
          empNumber: this.empNumber,
          limit: 100,
          offset: 0,
          sortField: 'date',
          sortOrder: 'DESC',
        });
        this.expenses = data.data;
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
        this.show = false;
      } finally {
        this.isLoading = false;
      }
    },
    async onAdd() {
      this.saving = true;
      try {
        const {data} = await expensaApi(
          `/reports/${this.reportId}/items`,
        ).create({
          expenseIds: this.checked.map((index) => this.rows[index].id),
        });
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_items_added'),
        });
        this.show = false;
        this.$emit('updated', data.data);
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
      } finally {
        this.saving = false;
      }
    },
    onCancel() {
      this.show = false;
    },
  },
};
</script>
